import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { classNames } from "primereact/utils";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import localeText from "../../../../../locale";
import { DEVICES, LANGS, SLOT_FIELDS } from "../bannerModel";

const deviceLabel = (device) => localeText(device === "desktop" ? "adminBannersDesktop" : "adminBannersMobile");

// Recorte na proporção do banner. A imagem recortada pode ser repetida em outros espaços
// do mesmo banner; a seleção começa vazia a cada recorte (no clássico ela ficava marcada).
const CropDialog = ({ crop, ratio, ratioLabel, onApply, onCancel }) => {
  const cropperRef = useRef(null);
  const [alsoFields, setAlsoFields] = useState([]);

  useEffect(() => {
    setAlsoFields([]);
  }, [crop]);

  if (!crop) return null;

  const otherSlots = SLOT_FIELDS.filter(({ field }) => field !== crop.field);
  const toggle = (field) =>
    setAlsoFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));

  const selectGroup = (predicate) => {
    const fields = otherSlots.filter(predicate).map(({ field }) => field);
    const allSelected = fields.every((field) => alsoFields.includes(field));
    setAlsoFields((prev) =>
      allSelected ? prev.filter((f) => !fields.includes(f)) : [...new Set([...prev, ...fields])],
    );
  };

  const apply = () => {
    const canvas = cropperRef.current?.cropper?.getCroppedCanvas();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "banner.png", { type: "image/png" });
      onApply(file, [crop.field, ...alsoFields]);
    }, "image/png");
  };

  const current = SLOT_FIELDS.find(({ field }) => field === crop.field);

  return (
    <Dialog
      visible
      onHide={onCancel}
      header={
        <div>
          <div className="admin-dialog__title">{localeText("cropImage")}</div>
          <div className="admin-dialog__subtitle">
            {`${deviceLabel(current.device)} · ${current.lang.toUpperCase()} · ${localeText(
              "adminBannersRatio",
            )} ${ratioLabel}`}
          </div>
        </div>
      }
      draggable={false}
      resizable={false}
      className="admin-dialog"
      style={{ width: "52rem" }}
      breakpoints={{ "960px": "calc(100vw - 1rem)" }}
      footer={
        <div className="admin-confirm__footer">
          <button type="button" className="admin-btn" onClick={onCancel}>
            {localeText("cancel")}
          </button>
          <button type="button" className="admin-btn admin-btn--primary" onClick={apply}>
            <i className="pi pi-check" aria-hidden="true" />
            {alsoFields.length
              ? `${localeText("adminBannersApplyTo")} ${alsoFields.length + 1}`
              : localeText("cropImage")}
          </button>
        </div>
      }
    >
      <div className="admin-form">
        <Cropper
          ref={cropperRef}
          src={crop.src}
          aspectRatio={ratio}
          viewMode={1}
          autoCropArea={1}
          background={false}
          style={{ height: 380, width: "100%" }}
        />

        <fieldset className="admin-form__fieldset">
          <legend className="admin-form__legend">{localeText("doYouWantToRepeatThisImage")}</legend>
          <div className="admin-banner-crop__quick">
            {DEVICES.map((device) => (
              <button
                key={device.key}
                type="button"
                className="admin-link-btn"
                onClick={() => selectGroup((slot) => slot.device === device.key)}
              >
                {`${localeText("adminBannersAllLanguages")} · ${deviceLabel(device.key)}`}
              </button>
            ))}
            <button
              type="button"
              className="admin-link-btn"
              onClick={() => selectGroup((slot) => slot.lang === current.lang)}
            >
              {`${current.lang.toUpperCase()} · ${localeText("adminBannersBothDevices")}`}
            </button>
          </div>
          <div className="admin-banner-crop__grid">
            {DEVICES.map((device) => (
              <div key={device.key} className="admin-banner-crop__row">
                <span className="admin-muted">{deviceLabel(device.key)}</span>
                {LANGS.map((lang) => {
                  const slot = otherSlots.find((s) => s.device === device.key && s.lang === lang.code);
                  if (!slot) {
                    return (
                      <span key={lang.code} className="admin-chip admin-chip--locked">
                        {lang.code.toUpperCase()}
                      </span>
                    );
                  }
                  const checked = alsoFields.includes(slot.field);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      className={classNames("admin-chip admin-banner-crop__chip", {
                        "admin-banner-crop__chip--on": checked,
                      })}
                      aria-pressed={checked}
                      onClick={() => toggle(slot.field)}
                    >
                      {checked && <i className="pi pi-check" aria-hidden="true" />}
                      {lang.code.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
};

export default CropDialog;
